import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';

import {
  Skeleton,
  Typography,
} from 'antd';
import { useNavigate } from 'react-router-dom';

import CommunityMessagesAPI from '../../api/CommunityMessageAPI';
import SearchBar from '../../components/Header/SearchBar';
import { setMessageRoomList } from '../../context/communityMessageReducer';
import { useTheme } from '../../context/ThemeProvider';
import showAppError from '../../shared/error';
import {
  useAppDispatch,
  useAppSelector,
} from '../../shared/hooks';
import { ROUTES } from '../../types/routes';
import CommunityMessageCard from './components/CommunityRoomCard';

type Props = {};

const MESSAGE_FILTERS = [
  { name: 'All', _id: 'all' },
  { name: 'Unread', _id: 'unread' },
];

const CommunityMessage: React.FC<Props> = () => {
  const navigate = useNavigate();
  const { colors } = useTheme();

  const dispatch = useAppDispatch();
  const { rooms } = useAppSelector((state) => state.communityMessage);
  const { groupChannelList } = useAppSelector((state) => state?.community);

  const [searchQuery, setSearchQuery] = useState('');
  const [selectedFilterValue, setSelectedFilterValue] = useState('all');
  const [loading, setLoading] = useState(false);

  const fetchMessageRoomList = useCallback(async () => {
    try {
      setLoading(true);
      const resp = await CommunityMessagesAPI.getMessageRoomList();
      if (resp?.status === 200) {
        dispatch(setMessageRoomList(resp?.data?.result));
      }
    } catch (error) {
      showAppError(error);
    } finally {
      setLoading(false);
    }
  }, [dispatch]);

  const filterRooms = useMemo(() => {
    let filteredRoom = rooms;
    if (searchQuery?.trim()) {
      filteredRoom = rooms.filter((eachRoom) => {
        let isSlectedFilterPass = true;
        if (selectedFilterValue === 'unread') {
          isSlectedFilterPass = Boolean(
            eachRoom?.unreadCount && eachRoom?.unreadCount > 0,
          );
        }

        if (selectedFilterValue !== 'all' && selectedFilterValue !== 'unread') {
          isSlectedFilterPass =
            eachRoom?.channel?.groupArr.includes(selectedFilterValue);
        }

        return (
          eachRoom?.channel?.name
            ?.toLowerCase()
            .includes(searchQuery?.toLowerCase()) && isSlectedFilterPass
        );
      });
      return filteredRoom;
    }

    if (selectedFilterValue === 'unread') {
      filteredRoom = filteredRoom.filter((eachRoom) =>
        Boolean(eachRoom?.unreadCount && eachRoom?.unreadCount > 0),
      );
    }

    if (selectedFilterValue !== 'all' && selectedFilterValue !== 'unread') {
      filteredRoom = filteredRoom.filter((eachRoom) =>
        eachRoom?.channel?.groupArr.includes(selectedFilterValue),
      );
    }

    return filteredRoom;
  }, [rooms, searchQuery, selectedFilterValue]);

  const displayRoomList = useCallback(() => {
    if (filterRooms?.length === 0) {
      return (
        <div className="noChatText">
          <Typography.Text>Oops! No chats found</Typography.Text>
        </div>
      );
    }
    return filterRooms.map((eachRoomData) => (
      <CommunityMessageCard
        roomData={eachRoomData}
        onClick={() => {
          navigate(
            ROUTES.COMMUNITY_MESSAGES_ROOM.replace(
              ':roomId',
              eachRoomData?._id,
            ),
          );
        }}
      />
    ));
  }, [filterRooms, navigate]);

  useEffect(() => {
    fetchMessageRoomList();
  }, [fetchMessageRoomList]);

  if (loading) {
    return (
      <div
        style={{
          width: '100%',
          padding: 10,
        }}>
        <div className="new-message_container">
          {Array(10)
            .fill(1)
            .map(() => (
              <Skeleton avatar paragraph={{ rows: 1 }} />
            ))}
        </div>
      </div>
    );
  }

  return (
    <>
      {/* {newMessage === 'true' ? <CommunityNewMessage /> : null} */}
      <div
        style={{
          width: '100%',
        }}>
        <div className="new-message_search">
          <SearchBar
            onDebounce={(value) => {
              if (setSearchQuery) {
                setSearchQuery(value);
              }
            }}
            placeholder="Search"
            style={{
              width: '100%',
              borderColor: colors.BORDER,
              padding: 0,
            }}
          />
        </div>
        {!loading ? (
          <div className="new-message_filter">
            {[...MESSAGE_FILTERS, ...groupChannelList].map((eachGroup) => {
              return (
                <button
                  className={`${
                    selectedFilterValue === eachGroup?._id
                      ? 'new-message_filter_selected'
                      : ''
                  } `}
                  key={eachGroup?._id}
                  onClick={() =>
                    setSelectedFilterValue(
                      selectedFilterValue === eachGroup?._id
                        ? 'all'
                        : eachGroup?._id,
                    )
                  }>
                  {eachGroup?.name}
                </button>
              );
            })}
          </div>
        ) : null}
        <div className="new-message_container">{displayRoomList()}</div>
      </div>
    </>
  );
};

export default CommunityMessage;
